/**
 * QuickEventCreator — fast event creation from entities.
 */
import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useAppStore } from '@/store/appStore';
import { Plus, Calendar } from 'lucide-react';

type Props = {
  onCreated?: (id: Id<'entities'>) => void;
};

export function QuickEventCreator({ onCreated }: Props) {
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const [name, setName] = useState('');
  const [venueId, setVenueId] = useState<Id<'entities'> | ''>('');
  const [expanded, setExpanded] = useState(false);

  const venues = useQuery(api.entities.list, { accountId, entityType: 'venue' });
  const artists = useQuery(api.entities.list, { accountId, entityType: 'artist' });
  const createEntity = useMutation(api.entities.create);

  const handleCreate = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      const id = await createEntity({
        accountId,
        entityType: 'event',
        name: n,
        displayName: n,
      });
      setName('');
      setExpanded(false);
      onCreated?.(id);
    } catch (err) {
      console.error('Create event failed:', err);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <Plus className="h-4 w-4" />
        Quick create event
      </button>
      {expanded && (
        <div className="mt-3 space-y-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Event name"
            className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={!name.trim()}
              className="flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Calendar className="h-4 w-4" />
              Create
            </button>
            <button
              onClick={() => setExpanded(false)}
              className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
